import { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core';
import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { getEntityRelations, useEntity } from '@backstage/plugin-catalog-react';
import { EntityDisplayLinks } from '../../shared/EntityDisplayLink';
import { toSafeHref } from '../../shared/links';
import { useByxT } from '../../shared/translations';
import { jetbrainsMono } from '../../theme/fonts';
import { byxScheme } from '../../theme/tokens';
import { ByxCard } from '../ByxCard';
import { ByxCardBoundary } from '../ByxCardBoundary';
import { ExternalLink } from '../ExternalLink';
import { providesApis, useProvidedGateway } from '../gatewayApis';

/** Styles of the Sobre cards (Componente, API, Squad, Sistema). */
export const useAboutStyles = makeStyles(theme => {
  const scheme = byxScheme(theme.palette.type);
  return {
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    description: {
      margin: 0,
      maxWidth: 720,
      fontSize: 14,
      lineHeight: '21px',
      color: scheme.mutedText,
    },
    fields: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
      gap: theme.spacing(2, 2.5),
      margin: 0,
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      lineHeight: '17px',
      color: scheme.mutedText,
    },
    value: {
      margin: 0,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '21px',
      color: scheme.ink,
      '& a': { color: scheme.accent },
    },
    mono: {
      fontFamily: jetbrainsMono,
      fontSize: 13,
      fontWeight: 500,
    },
    tags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    tag: {
      padding: '2px 8px',
      borderRadius: 6,
      fontFamily: jetbrainsMono,
      fontSize: 12,
      lineHeight: '18px',
      backgroundColor: scheme.chip,
      color: scheme.chipInk,
    },
  };
});

const GITHUB_SLUG = /^[\w.-]+\/[\w.-]+$/;

/** "<org>/<repo>" and its GitHub address, from `github.com/project-slug`. */
export function repositorySlug(
  entity: Entity,
): { slug: string; href: string } | undefined {
  const slug = entity.metadata.annotations?.['github.com/project-slug']?.trim();
  if (!slug || !GITHUB_SLUG.test(slug)) {
    return undefined;
  }
  const href = toSafeHref(`https://github.com/${slug}`);
  return href ? { slug, href } : undefined;
}

const useStyles = useAboutStyles;

/** One Sobre field: its label, then its value (one line, ellipsis). */
export function AboutField(
  props: Readonly<{ label: string; children: ReactNode }>,
) {
  const classes = useStyles();
  return (
    <div className={classes.field}>
      <dt className={classes.label}>{props.label}</dt>
      <dd className={classes.value}>{props.children}</dd>
    </div>
  );
}

/** "Domínio no gateway", from the provided gateway API (AC-1). */
function GatewayDomainField() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const classes = useStyles();
  const state = useProvidedGateway(entity);
  if (state.status !== 'ready' || state.value.domains.length === 0) {
    return null;
  }
  const domains = state.value.domains.join(', ');
  return (
    <AboutField label={t('entity.about.domain')}>
      <span className={classes.mono} title={domains}>
        {domains}
      </span>
    </AboutField>
  );
}

/** The entity's tags, without empty values. */
export function tagsOf(entity: Entity): string[] {
  return (entity.metadata.tags ?? []).filter(
    tag => typeof tag === 'string' && tag !== '',
  );
}

function AboutBody() {
  const { entity } = useEntity();
  const { t } = useByxT();
  const classes = useStyles();
  const description = entity.metadata.description?.trim();
  const owners = getEntityRelations(entity, RELATION_OWNED_BY);
  const systems = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const repository = repositorySlug(entity);
  const hasGateway = providesApis(entity);
  const tags = tagsOf(entity);
  const hasFields =
    owners.length > 0 || systems.length > 0 || !!repository || hasGateway;

  if (!description && !hasFields && tags.length === 0) {
    return null;
  }

  return (
    <ByxCard title={t('entity.about.title')}>
      <div className={classes.body}>
        {description && <p className={classes.description}>{description}</p>}
        {hasFields && (
          <dl className={classes.fields}>
            {owners.length > 0 && (
              <AboutField label={t('entity.about.owner')}>
                <EntityDisplayLinks entityRefs={owners} defaultKind="group" />
              </AboutField>
            )}
            {systems.length > 0 && (
              <AboutField label={t('entity.about.system')}>
                <EntityDisplayLinks entityRefs={systems} defaultKind="system" />
              </AboutField>
            )}
            {hasGateway && <GatewayDomainField />}
            {repository && (
              <AboutField label={t('entity.about.repository')}>
                <ExternalLink
                  href={repository.href}
                  title={repository.slug}
                  accessibleName={t('entity.about.repositoryLabel', {
                    repo: repository.slug,
                  })}
                >
                  {repository.slug}
                </ExternalLink>
              </AboutField>
            )}
          </dl>
        )}
        {tags.length > 0 && (
          <ul className={classes.tags} aria-label={t('entity.about.tags')}>
            {tags.map(tag => (
              <li key={tag} className={classes.tag}>
                {tag}
              </li>
            ))}
          </ul>
        )}
      </div>
    </ByxCard>
  );
}

/**
 * Sobre (UI spec): the description, then Dono · Sistema · Domínio no
 * gateway · Repositório (a field without a value is not rendered), then
 * every tag. Linguagens and Épico have no producer in the catalog yet, so
 * they are not rendered. Hidden when there is nothing to show.
 */
export function AboutCard() {
  const { t } = useByxT();
  return (
    <ByxCardBoundary title={t('entity.about.title')}>
      <AboutBody />
    </ByxCardBoundary>
  );
}
